import { Box, Card, CardContent, Typography, Link, Chip } from '@mui/material';
import { getStoreStatus } from '../../utils/storeHours';
import { toTelHref } from '../../utils/phone';

interface StoreInfoProps {
  store: any;
  loading: boolean;
  primaryColor: string;
  t: (key: string) => string;
}

export const StoreInfo = ({ store, loading, primaryColor, t }: StoreInfoProps) => {

  if (!store) return null;

  const storeStatus = store.openingHours ? getStoreStatus(store.openingHours) : null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        <i className="bx bx-store" style={{ fontSize: 24, color: primaryColor }} />
        {t('pages.dashboard.myStore')}
      </Typography>
      <Card>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              {store.name && (
                <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {store.name}
                  </Typography>
                  {storeStatus && (
                    <Chip 
                      label={storeStatus.isOpen ? t('pages.myStore.open') : t('pages.myStore.closed')}
                      size="small"
                      sx={{ bgcolor: storeStatus.isOpen ? '#4caf50' : '#f44336', color: 'white', fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
              )}
              
              {(store.AddressResponse?.address1 || store.AddressResponse?.city) && (
                <Box sx={{ mb: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <i className="bx bx-map-pin" style={{ fontSize: 18, color: primaryColor, flexShrink: 0, marginTop: 2 }} />
                  <Box sx={{ flex: 1 }}>
                    {store.AddressResponse?.address1 && <Typography variant="body2">{store.AddressResponse.address1}</Typography>}
                    {store.AddressResponse?.address2 && <Typography variant="body2">{store.AddressResponse.address2}</Typography>}
                    {(store.AddressResponse?.zipCode || store.AddressResponse?.city) && (
                      <Typography variant="body2">
                        {store.AddressResponse.zipCode} {store.AddressResponse.city}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            <Box>
              {store.PhoneResponse?.number && (
                <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <i className="bx bx-phone" style={{ fontSize: 18, color: primaryColor, flexShrink: 0 }} />
                  <Link
                    href={toTelHref(store.PhoneResponse.number, store.PhoneResponse.dialingCode)}
                    variant="body2"
                    sx={{ color: primaryColor, textDecoration: 'none', fontWeight: 500 }}
                  >
                    {store.PhoneResponse.number}
                  </Link>
                </Box>
              )}
              
              {store.EmailResponse?.emailValue && (
                <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <i className="bx bx-envelope" style={{ fontSize: 18, color: primaryColor, flexShrink: 0 }} />
                  <Link 
                    href={`mailto:${store.EmailResponse.emailValue}`}
                    variant="body2"
                    sx={{ color: primaryColor, textDecoration: 'none', fontWeight: 500 }}
                  >
                    {store.EmailResponse.emailValue}
                  </Link>
                </Box>
              )}
              
              {store.openingHours && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <i className="bx bx-time" style={{ fontSize: 18, color: primaryColor, flexShrink: 0, marginTop: 2 }} />
                  <Box sx={{ flex: 1 }}>
                    {store.openingHours.split(/[,\n]+/).filter((h: string) => h.trim()).map((hour: string, index: number) => (
                      <Typography 
                        key={index} 
                        variant="body2" 
                        sx={{ 
                          color: 'text.primary', 
                          whiteSpace: 'nowrap',
                          lineHeight: 1.6
                        }}
                      >
                        {hour.trim()}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
